import Title from "common/Typography/Title";
import ChartCardLayout from "common/Layouts/ChartCardLayout";
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useGetBotsGraphsData } from "@shared/features/bots/hooks";
import { useIncludeArchived } from "features/filters/hooks";
import LineChart from "common/Charts/LineChart/LineChart";
import { FormattedBotsGraphsData } from "@shared/types";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import styled from "styled-components";
import BarChart from "common/Charts/BarChart";
import Card from "common/Boxes/Card";
import { TIME_RANGE_1_MONTHS } from "@shared/common/constants";
import { BotType, TimeRange } from "@shared/types";

interface BotsGraphLayoutProps {
  title: string;
  botType: BotType;
  graphType: keyof FormattedBotsGraphsData;
  useBarChart?: boolean;
  isLoading?: boolean;
}

const BotsGraphCard = forwardRef(
  (
    { title, botType, useBarChart, graphType, isLoading }: BotsGraphLayoutProps,
    ref
  ) => {
    const displayCurrency = useSelector(selectDisplayCurrency);
    const { getDisplayCurrencyAmountPrecision } = usePrecisions();
    const profitPrecision = getDisplayCurrencyAmountPrecision();
    const [timeRange, setTimeRange] = useState<TimeRange>(TIME_RANGE_1_MONTHS);
    const includeArchived = useIncludeArchived(botType);

    const getBotsGraphsDataArgs = useMemo(() => {
      return {
        bot_type: botType,
        time_range: timeRange,
        include_archived: includeArchived,
      };
    }, [botType, timeRange, includeArchived]);

    const graphData = useGetBotsGraphsData(getBotsGraphsDataArgs);

    useImperativeHandle(ref, () => ({
      refetch: () => {
        try {
          graphData.refetch();
        } catch {}
      },
    }));

    const graphDataByType = graphData.data[graphType];
    const loading = graphData.status.isFetching || isLoading;
    const isSuccess = graphData.status.isSuccess;

    const chart = (
      <ChartWrapper>
        {useBarChart ? (
          <BarChart
            data={graphDataByType}
            loading={loading}
            displayCurrency={displayCurrency}
            usePositiveNegativeColorScheme={true}
            precision={profitPrecision}
            isSuccess={isSuccess}
          />
        ) : (
          <LineChart
            data={graphDataByType}
            loading={loading}
            displayCurrency={displayCurrency}
            precision={profitPrecision}
            isSuccess={isSuccess}
          />
        )}
      </ChartWrapper>
    );

    return (
      <ChartCard>
        <ChartCardLayout
          change={<Title level={3}>{title}</Title>}
          chart={chart}
          selectedTimeRange={timeRange}
          setTimeRange={setTimeRange}
          buttonSize="medium"
          mobileBreakpoint={480}
          autoLayoutHeight={true}
        />
      </ChartCard>
    );
  }
);

export default React.memo(BotsGraphCard);

const ChartCard = styled(Card)`
  overflow: hidden;
`;
const ChartWrapper = styled.div`
  height: 200px;
  margin-top: 8px;
`;
